import { useEffect, useMemo, useState } from 'react';
import { Button, Combobox, Field, Modal, type ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api } from '@/lib/api';
import type { EmailThread } from '@/lib/types';
import { triageErrorMessage } from './email-log-format';

/*
 * "Assign to company" for an unmatched / ambiguous / ignored email thread —
 * PATCH /email-log/threads/:id {companyId} (`email-log.triage`). The thread's
 * candidate companies are offered first, then a server-side company search.
 */

const OPTION_PER_PAGE = 50;

function useCompanySearch(enabled: boolean) {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  const loading = enabled && loadedTerm !== term;

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listCompanies(
        {
          page: 1,
          perPage: OPTION_PER_PAGE,
          sort: 'name',
          order: 'asc',
          ...(term ? { search: term } : {}),
        },
        signal,
      )
      .then((result) => {
        if (signal.aborted) return;
        setOptions(result.data.map((company) => ({ value: String(company.id), label: company.name })));
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });
    return () => controller.abort();
  }, [enabled, term]);

  return { options, loading, failed, setTerm };
}

export function AssignEmailThreadDialog({
  thread,
  onClose,
  onAssigned,
}: {
  thread: EmailThread | null;
  onClose: () => void;
  onAssigned: (updated: EmailThread) => void;
}) {
  const open = thread !== null;
  return open ? (
    <AssignBody key={thread.id} thread={thread} onClose={onClose} onAssigned={onAssigned} />
  ) : null;
}

function AssignBody({
  thread,
  onClose,
  onAssigned,
}: {
  thread: EmailThread;
  onClose: () => void;
  onAssigned: (updated: EmailThread) => void;
}) {
  const [companyId, setCompanyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const search = useCompanySearch(true);

  const options = useMemo<ComboboxOption[]>(() => {
    const candidates = thread.candidateCompanies.map((company) => ({
      value: String(company.id),
      label: company.name,
      hint: 'Candidate',
    }));
    const seen = new Set(candidates.map((option) => option.value));
    return [...candidates, ...search.options.filter((option) => !seen.has(option.value))];
  }, [thread.candidateCompanies, search.options]);

  const submit = () => {
    const id = Number(companyId);
    if (!Number.isInteger(id) || id <= 0) return;
    setBusy(true);
    setError(null);
    api
      .assignEmailThread(thread.id, id)
      .then((res) => {
        onAssigned(res.data);
      })
      .catch((caught: unknown) => {
        setError(triageErrorMessage(caught, 'Failed to assign the thread'));
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title="Assign to company"
      description={thread.subject || '(no subject)'}
      size="sm"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={busy}
            disabled={companyId === ''}
            tooltip={companyId === '' ? 'Choose a company first' : undefined}
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label="Company">
          {({ id }) => (
            <Combobox
              id={id}
              value={companyId}
              onChange={setCompanyId}
              options={options}
              placeholder="Choose a company"
              searchPlaceholder="Search companies…"
              onSearch={search.setTerm}
              loading={search.loading}
            />
          )}
        </Field>
        {search.failed && (
          <Notice tone="danger" role="alert">
            Couldn&rsquo;t load companies.
          </Notice>
        )}
        {error && (
          <Notice tone="danger" role="alert">
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}
      </div>
    </Modal>
  );
}
